<template>
  <div>
    <div class="header">
      <title-vue title="Tutoriais" />
      <v-btn dark color="#e5183d" @click="openForm()">Novo tutorial</v-btn>
    </div>

    <v-dialog v-model="formDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>{{ editingId ? 'Editar tutorial' : 'Novo tutorial' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="form.title"
              label="Título *"
              :rules="[v => !!v || 'Título é obrigatório']"
              outlined
              dense
            />
            <v-text-field
              v-model="form.link"
              label="Link *"
              :rules="[v => !!v || 'Link é obrigatório']"
              outlined
              dense
            />
            <v-select
              v-model="form.category"
              :items="categoryOptions"
              item-text="text"
              item-value="value"
              label="Categoria *"
              :rules="[v => !!v || 'Categoria é obrigatória']"
              outlined
              dense
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="formDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="formSaving" :disabled="!formValid" @click="saveTutorial">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card>
        <v-card-title>Excluir tutorial</v-card-title>
        <v-card-text>
          Deseja realmente excluir o tutorial "{{ tutorialToDelete?.title }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Não</v-btn>
          <v-btn color="error" :loading="deleteSaving" @click="confirmDelete">Sim</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-skeleton-loader v-if="loading" tile type="card" class="mx-auto elevation-0" />
    <v-card v-else>
      <v-card-title>
        <v-row>
          <v-col cols="12" md="9">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquise por título ou link"
              clearable
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="tutorials"
        :search="search"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-text': 'tutoriais por página' }"
        no-data-text="Nenhum tutorial cadastrado"
        class="elevation-0"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td class="d-block d-sm-table-cell">{{ item.title }}</td>
              <td class="d-block d-sm-table-cell">
                <a :href="item.link" target="_blank" rel="noopener noreferrer">{{ item.link }}</a>
              </td>
              <td class="d-block d-sm-table-cell">{{ getCategoryLabel(item.category) }}</td>
              <td class="d-block d-sm-table-cell">
                <v-icon small class="mr-2" @click="openForm(item)">mdi-pencil</v-icon>
                <v-icon small color="error" @click="openDelete(item)">mdi-delete</v-icon>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import TitleVue from "@/components/title/Title.vue";
import { GetTutorials } from "@/services/tutorials/GetTutorials";
import { CreateTutorial } from "@/services/tutorials/CreateTutorial";
import { UpdateTutorial } from "@/services/tutorials/UpdateTutorial";
import { DeleteTutorial } from "@/services/tutorials/DeleteTutorial";
import type { TutorialType, TutorialForm } from "@/types/tutorial/Tutorial";
import {
  TUTORIAL_CATEGORY_OPTIONS,
  TutorialCategoryLabel,
} from "@/types/tutorial/Category";
import { TutorialCategory } from "@/types/tutorial/Category";

export default defineComponent({
  components: { TitleVue },

  data() {
    return {
      loading: true,
      tutorials: [] as TutorialType[],
      search: "",
      headers: [
        { text: "Título", value: "title" },
        { text: "Link", value: "link" },
        { text: "Categoria", value: "category" },
        { text: "Ações", value: "actions", sortable: false },
      ],
      categoryOptions: TUTORIAL_CATEGORY_OPTIONS,
      formDialog: false,
      formSaving: false,
      formValid: false,
      editingId: null as string | null,
      form: {
        title: "",
        link: "",
        category: TutorialCategory.CONSUMIDOR,
      } as TutorialForm,
      deleteDialog: false,
      deleteSaving: false,
      tutorialToDelete: null as TutorialType | null,
    };
  },

  methods: {
    getCategoryLabel(category: TutorialCategory): string {
      return TutorialCategoryLabel[category] ?? category;
    },
    resetForm() {
      this.form = {
        title: "",
        link: "",
        category: TutorialCategory.CONSUMIDOR,
      };
      this.editingId = null;
    },
    openForm(item?: TutorialType) {
      this.resetForm();
      if (item) {
        this.editingId = item.id;
        this.form = {
          title: item.title,
          link: item.link,
          category: item.category,
        };
      }
      this.formDialog = true;
    },
    async saveTutorial() {
      const form = this.$refs.form as { validate: () => boolean } | undefined;
      if (form && !form.validate()) return;
      this.formSaving = true;
      try {
        if (this.editingId) {
          const updateTutorial = new UpdateTutorial();
          await updateTutorial.execute(this.editingId, this.form);
          const t = this.tutorials.find((x) => x.id === this.editingId);
          if (t) {
            t.title = this.form.title;
            t.link = this.form.link;
            t.category = this.form.category;
          }
          this.$store.dispatch("showSnack", {
            message: "Tutorial atualizado com sucesso.",
            color: "success",
          });
        } else {
          const createTutorial = new CreateTutorial();
          const id = await createTutorial.execute(this.form);
          this.tutorials.push({
            id,
            ...this.form,
          });
          this.$store.dispatch("showSnack", {
            message: "Tutorial cadastrado com sucesso.",
            color: "success",
          });
        }
        this.formDialog = false;
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao salvar tutorial.",
          color: "error",
        });
      } finally {
        this.formSaving = false;
      }
    },
    openDelete(item: TutorialType) {
      this.tutorialToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.tutorialToDelete) return;
      this.deleteSaving = true;
      try {
        const deleteTutorial = new DeleteTutorial();
        await deleteTutorial.execute(this.tutorialToDelete.id);
        this.tutorials = this.tutorials.filter((t) => t.id !== this.tutorialToDelete!.id);
        this.$store.dispatch("showSnack", {
          message: "Tutorial excluído.",
          color: "success",
        });
        this.deleteDialog = false;
        this.tutorialToDelete = null;
      } catch {
        this.$store.dispatch("showSnack", {
          message: "Erro ao excluir tutorial.",
          color: "error",
        });
      } finally {
        this.deleteSaving = false;
      }
    },
    async loadTutorials() {
      this.loading = true;
      try {
        const getTutorials = new GetTutorials();
        this.tutorials = await getTutorials.execute();
      } catch {
        this.tutorials = [];
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.loadTutorials();
  },
});
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

a {
  color: inherit;
  text-decoration: underline;
}
</style>
